import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { ExportPostsInput, ExportPostsResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * exportPosts callable hook (admin 第 14 弾、Phase 5 REQ-4.3.14 / 4.3.18 / 9.2.1)。
 *
 * 旧 `old/apps/admin/src/pages/ExportPage.tsx` の client 直接 fetch を v2 callable に統合。
 * Hook 自体は `useSetRoleClaim` と同 pattern (idle / pending / success / error 4 状態)。
 *
 * download 起動は本 hook の責務外 (page 側で Blob 生成 + a.click)。
 */

export type UseExportPostsState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: ExportPostsResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseExportPostsReturn {
  state: UseExportPostsState;
  call: (input: ExportPostsInput) => Promise<ExportPostsResult | null>;
  reset: () => void;
}

export function useExportPosts(): UseExportPostsReturn {
  const [state, setState] = useState<UseExportPostsState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: ExportPostsInput): Promise<ExportPostsResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<ExportPostsInput, ExportPostsResult>(functions, 'exportPosts');
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}

/**
 * CSV / GeoJSON content をブラウザ download として起動する helper。
 * CSV のみ UTF-8 BOM を先頭付加 (Excel での文字化け回避、旧 ExportPage と同 logic)。
 */
export function triggerDownload(result: ExportPostsResult): void {
  const mime =
    result.format === 'csv' ? 'text/csv;charset=utf-8' : 'application/geo+json;charset=utf-8';
  const parts = result.format === 'csv' ? ['﻿', result.content] : [result.content];
  const blob = new Blob(parts, { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}
